// M013 Phase 13.5a+b: Personas tab.
//
// Renders the project's agent roster as Pokemon-style trading cards
// with Stardew-Valley-style pixel-art headshot portraits (Captain
// directive 2026-04-23). Portrait lands via the orchestrator's
// avatar_service (R2-backed); a placeholder silhouette shows while
// generation is pending. Re-roll button overlays the portrait.
//
// Card geometry: 192×192 portrait zone at top, seat_key top-right
// ("card number" style), display_name hero under portrait, role_title
// subtitle, model dropdown + score_state badge as the "stat block" at
// card footer. Two-stage armed delete. Accent inner frame.

import { TabScrollbox } from "../../chrome/TabScrollbox";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTable } from "spacetimedb/react";
import {
  ApiError,
  createAgent,
  deleteAgent,
  type ModelBacking,
  regenerateAgentAvatar,
  setAgentModel,
  setAgentMuted,
} from "../../api/client";
import {
  fetchOpenRouterModels,
  formatModelAge,
  formatModelPrice,
  type OpenRouterModel,
} from "../../api/openrouter";
import { tables } from "../../../spacetimedb-bindings";
import { AlertTriangle, RefreshCw, Trash2, User } from "lucide-react";
import { ModelAutocomplete } from "./ModelAutocomplete";
import { PersonaCandidateGallery } from "../PersonaCandidateGallery";
import { PersonaContractsPanel } from "../PersonaContractsPanel";
import { triggerBalanceRefresh } from "../../chrome/OpenRouterBalance";

interface Props {
  workspaceId: number;
  orgId: number | null;
}

interface AgentCard {
  agent_id: number;
  workspace_id: number;
  seat_key: string;
  display_name: string;
  role_title: string;
  score_state: string;
  avatar_url: string;
  created_at: number;
  prompt: string;
  // A08:
  kind: string;
  trigger_json: string;
  muted: boolean;
  model_backing: string;
}

type TriggerTemplateId =
  | "input_gate"
  | "architecture_drift"
  | "best_practice"
  | "ui_tweak"
  | "custom";

interface TriggerTemplate {
  id: TriggerTemplateId;
  label: string;
  hint: string;
  role_title_default: string;
  prompt_default: string;
  trigger_json: string;
}

const TRIGGER_TEMPLATES: TriggerTemplate[] = [
  {
    id: "input_gate",
    label: "Input gate",
    hint: "Fires synchronously on every Captain post. Classifies scope-change vs clarification vs interrupt.",
    role_title_default: "Input-gate watcher",
    prompt_default:
      "Classify the Captain's last post as one of {scope-change, clarification, style, interrupt} and either queue for the next Step, answer directly, or signal the working agent to pause.",
    trigger_json: `{"event":"captain_post","filter":{},"fire_limit_per_turn":1}`,
  },
  {
    id: "architecture_drift",
    label: "Architecture drift",
    hint: "Fires when the working agent runs commands outside the current section's scope.",
    role_title_default: "Architecture-drift watcher",
    prompt_default:
      "You guard the section's declared scope. When the working agent edits files outside the scope path, post a one-sentence 'you're out of scope' note so they can correct on the next turn.",
    trigger_json: `{"event":"working_agent_turn","filter":{"scope_path":"src/"},"fire_limit_per_turn":1}`,
  },
  {
    id: "best_practice",
    label: "Best-practice injector",
    hint: "Fires when the turn body carries uncertainty markers (should I, not sure, etc.). Injects distilled guidance.",
    role_title_default: "Best-practice injector",
    prompt_default:
      "When the working agent signals uncertainty, return a 1-2 sentence distillation of the relevant best practice. Keep it terse and cite the source concretely.",
    trigger_json: `{"event":"working_agent_turn","filter":{"uncertainty_markers":true},"fire_limit_per_turn":1}`,
  },
  {
    id: "ui_tweak",
    label: "UI-tweak auditor",
    hint: "Fires on every turn that carries a screenshot. Reviews against the design system.",
    role_title_default: "UI-tweak auditor",
    prompt_default:
      "Review the screenshot against Waytide's design system. Call out alignment, spacing, and colour-token violations in 3-5 terse bullets.",
    trigger_json: `{"event":"screenshot_attachment","filter":{},"fire_limit_per_turn":1}`,
  },
  {
    id: "custom",
    label: "Custom JSON",
    hint: "Write your own trigger blob. See docs/Agendas/a08_watchers.md for the shape.",
    role_title_default: "Custom watcher",
    prompt_default: "",
    trigger_json: `{"event":"working_agent_turn","filter":{},"fire_limit_per_turn":1}`,
  },
];

function bigintToNumber(v: unknown): number {
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "number") return v;
  return 0;
}

const SCORE_TONE: Record<string, string> = {
  new: "bg-surface text-muted",
  watching: "bg-warning/20 text-warning",
  trusted: "bg-success/20 text-success",
};

const MODEL_BACKING_OPTIONS: Array<{
  value: ModelBacking;
  label: string;
}> = [
  { value: "openrouter_key", label: "OpenRouter (BYOK)" },
  { value: "anthropic_key", label: "Anthropic API (BYOK)" },
  { value: "openai_key", label: "OpenAI API (BYOK)" },
  { value: "google_key", label: "Google API (BYOK)" },
  { value: "subscription", label: "Subscription (Claude / Codex)" },
  { value: "pooled", label: "Pooled (Premium)" },
];

export function PersonasTab({ workspaceId, orgId }: Props) {
  const [agentRows] = useTable(tables.agent);
  const [seatRows] = useTable(tables.team_recipe_seat);
  const [models, setModels] = useState<OpenRouterModel[]>([]);

  useEffect(() => {
    void fetchOpenRouterModels().then(setModels).catch(() => {});
  }, []);

  const agents = useMemo<AgentCard[]>(
    () =>
      agentRows
        .filter((a) => bigintToNumber(a.workspaceId) === workspaceId)
        // Direct-model seats (seat_key contains "/") are auto-
        // provisioned when the Captain @vendor/model-mentions in
        // chat. They're not personas the Captain created; the
        // Models tab is where those live. Hide them here.
        .filter((a) => !a.seatKey.includes("/"))
        .map((a) => ({
          agent_id: bigintToNumber(a.agentId),
          workspace_id: bigintToNumber(a.workspaceId),
          seat_key: a.seatKey,
          display_name: a.displayName,
          role_title: a.roleTitle,
          score_state: a.scoreState,
          avatar_url: a.avatarUrl ?? "",
          created_at: bigintToNumber(a.createdAt),
          prompt: a.prompt ?? "",
          kind: (a as unknown as { kind?: string }).kind ?? "working",
          trigger_json:
            (a as unknown as { triggerJson?: string }).triggerJson ?? "",
          muted: (a as unknown as { muted?: boolean }).muted ?? false,
          model_backing:
            (a as unknown as { modelBacking?: string }).modelBacking ?? "",
        }))
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [agentRows, workspaceId],
  );

  // Captain 2026-04-26: a watcher is just an agent with a trigger
  // condition. Listing is one grid; creation paths still differ
  // because the trigger contract is its own form.
  const sortedAgents = useMemo(
    () =>
      [...agents].sort((a, b) => {
        // Watchers below working agents within the same alphabetical
        // bucket — keeps the most-used cards first without splitting
        // the grid into two sections.
        const ak = a.kind === "watcher" ? 1 : 0;
        const bk = b.kind === "watcher" ? 1 : 0;
        if (ak !== bk) return ak - bk;
        return a.display_name.localeCompare(b.display_name);
      }),
    [agents],
  );

  const captainBuiltAgents = useMemo(
    () =>
      sortedAgents.filter(
        (agent) =>
          agent.kind !== "coordinator" && agent.model_backing !== "staff_pool",
      ),
    [sortedAgents],
  );

  const councilAgents = useMemo(
    () =>
      sortedAgents.filter(
        (agent) =>
          agent.kind === "coordinator" || agent.model_backing === "staff_pool",
      ),
    [sortedAgents],
  );

  const captainWatchers = useMemo(
    () => captainBuiltAgents.filter((agent) => agent.kind === "watcher"),
    [captainBuiltAgents],
  );

  const councilWatchers = useMemo(
    () => councilAgents.filter((agent) => agent.kind === "watcher"),
    [councilAgents],
  );

  const seatModelByAgent = useMemo<Map<number, string>>(() => {
    const m = new Map<number, string>();
    for (const row of seatRows) {
      const agentId = bigintToNumber(row.agentId);
      const model = typeof row.model === "string" ? row.model : "";
      if (agentId && model) m.set(agentId, model);
    }
    return m;
  }, [seatRows]);

  const [createOpen, setCreateOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  return (
    <TabScrollbox>
      <header className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-muted">
            Your team. Any persona can carry a trigger condition —
            "watcher" is just a role, not a separate type. Pearl lives
            in the drawer; click any handle on the right to ask her for
            help.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreateOpen((v) => !v)}
            className="cursor-pointer rounded-md border border-edge bg-surface-raised px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:border-accent"
            data-testid="personas-new-agent"
          >
            {createOpen ? "Cancel" : "+ New persona"}
          </button>
          {orgId != null && (
            <button
              onClick={() => setGalleryOpen(true)}
              className="cursor-pointer rounded-md border border-accent bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent transition-colors hover:bg-accent/20"
              data-testid="personas-pearl-gallery"
            >
              Pearl: pick from 4
            </button>
          )}
        </div>
      </header>

      {createOpen && (
        <CreatePersonaForm
          workspaceId={workspaceId}
          models={models}
          onDone={() => setCreateOpen(false)}
        />
      )}

      {galleryOpen && orgId != null && (
        <PersonaCandidateGallery
          workspaceId={workspaceId}
          orgId={orgId}
          defaultModel="anthropic/claude-sonnet-4.6"
          onClose={() => setGalleryOpen(false)}
        />
      )}

      <section className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <SummaryCard
          label="Your personas"
          value={String(captainBuiltAgents.length)}
          note={
            captainWatchers.length > 0
              ? `${captainWatchers.length} triggered`
              : "builders and specialists"
          }
        />
        <SummaryCard
          label="Waytide council"
          value={String(councilAgents.length)}
          note={
            councilWatchers.length > 0
              ? `${councilWatchers.length} default watchers`
              : "coordination layer"
          }
        />
        <SummaryCard
          label="Contract coverage"
          value={captainBuiltAgents.length === 0 ? "0" : "A20"}
          note="lanes, evidence, rubric, memory"
        />
      </section>

      <section className="mb-8 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2 border-b border-edge pb-1">
            <h3 className="text-sm font-semibold text-primary">Your team</h3>
            <span className="font-code text-[0.625rem] text-faint">
              {captainBuiltAgents.length} seat
              {captainBuiltAgents.length === 1 ? "" : "s"}
            </span>
          </div>
          <p className="text-[0.625rem] text-muted">
            Personas you chose, tuned, and pay for. A watcher is just a
            persona with a trigger contract.
          </p>
        </div>

        {captainBuiltAgents.length === 0 && !createOpen && (
          <div className="rounded-md border border-edge bg-surface-raised px-6 py-8 text-center">
            <p className="text-sm text-muted">
              No captain-built personas yet. Add your first from + New
              persona, or ask Pearl for four candidates.
            </p>
          </div>
        )}

        {captainBuiltAgents.length > 0 && (
          <div
            className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
            data-testid="personas-grid"
          >
            {captainBuiltAgents.map((agent) => (
              <PersonaCard
                key={agent.agent_id}
                agent={agent}
                currentModel={seatModelByAgent.get(agent.agent_id) ?? ""}
                models={models}
                orgId={orgId}
              />
            ))}
          </div>
        )}

        <PersonaContractsPanel
          workspaceId={workspaceId}
          agents={captainBuiltAgents}
        />
      </section>

      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2 border-b border-edge pb-1">
            <h3 className="text-sm font-semibold text-primary">
              Waytide council
            </h3>
            <span className="font-code text-[0.625rem] text-faint">
              seeded into every workspace
            </span>
          </div>
          <p className="text-[0.625rem] text-muted">
            Vela and the default safety/watch surfaces. They keep the room
            coherent, but they are not the same thing as your delivery team.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {councilAgents.map((agent) => (
            <PersonaCard
              key={agent.agent_id}
              agent={agent}
              currentModel={seatModelByAgent.get(agent.agent_id) ?? ""}
              models={models}
              orgId={orgId}
            />
          ))}
        </div>
      </section>
    </TabScrollbox>
  );
}

// Captain canon 2026-04-26: there's only one type of persona — whether
// it triggers on conditions is configuration, not a separate type.
// Single create form; "Add trigger condition" is a disclosure that
// reveals the template + trigger_json fields. On submit, if a trigger
// is set we hit /watchers; otherwise /agents. The backend route split
// is internal; the user surface is one persona shape.
function CreatePersonaForm({
  workspaceId,
  models,
  onDone,
}: {
  workspaceId: number;
  models: OpenRouterModel[];
  onDone: () => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("");
  const [modelBacking, setModelBacking] =
    useState<ModelBacking>("openrouter_key");
  const [triggerEnabled, setTriggerEnabled] = useState(false);
  const [template, setTemplate] = useState<TriggerTemplateId>("input_gate");
  const [triggerJson, setTriggerJson] = useState(
    TRIGGER_TEMPLATES.find((t) => t.id === "input_gate")!.trigger_json,
  );
  const [triggerOverridden, setTriggerOverridden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // When the user picks a different template, refresh prompt + trigger
  // defaults — but only the trigger if they haven't typed a custom one.
  useEffect(() => {
    if (!triggerEnabled) return;
    const next = TRIGGER_TEMPLATES.find((t) => t.id === template)!;
    if (!roleTitle.trim()) setRoleTitle(next.role_title_default);
    if (!prompt.trim()) setPrompt(next.prompt_default);
    if (!triggerOverridden) setTriggerJson(next.trigger_json);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, triggerEnabled]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!displayName.trim() || !model.trim()) {
      setErr("Display name and model are both required.");
      return;
    }
    if (triggerEnabled) {
      if (!triggerJson.trim()) {
        setErr("Trigger JSON is required when 'Add trigger' is enabled.");
        return;
      }
      try {
        JSON.parse(triggerJson);
      } catch {
        setErr("Trigger JSON is not valid JSON.");
        return;
      }
    }
    setBusy(true);
    setErr("");
    try {
      // Single unified create call. trigger_json populated → reducer
      // sets kind="watcher"; empty → kind="working". model_backing is
      // sent as "" for triggered personas (they ride the staff pool
      // today; a future orchestrator change can respect per-persona
      // backing for watchers too).
      await createAgent(
        workspaceId,
        displayName.trim(),
        roleTitle.trim(),
        prompt,
        model,
        triggerEnabled ? "" : modelBacking,
        triggerEnabled ? triggerJson : undefined,
        triggerEnabled ? 1 : undefined,
      );
      setDisplayName("");
      setRoleTitle("");
      setPrompt("");
      setModel("");
      setTriggerEnabled(false);
      setTriggerOverridden(false);
      onDone();
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Could not create this persona. Check the inputs and retry.",
      );
    } finally {
      setBusy(false);
    }
  }

  const tpl = TRIGGER_TEMPLATES.find((t) => t.id === template)!;
  return (
    <form
      onSubmit={submit}
      className="mb-4 flex flex-col gap-3 rounded-md border border-edge bg-surface-raised p-4"
      data-testid="personas-create-form"
    >
      <h3 className="text-sm font-semibold text-primary">New persona</h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={64}
            className="rounded-md border border-edge bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:border-border-highlight"
            data-testid="personas-create-name"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Role</span>
          <input
            type="text"
            value={roleTitle}
            onChange={(e) => setRoleTitle(e.target.value)}
            maxLength={64}
            placeholder="e.g. Backend engineer"
            className="rounded-md border border-edge bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:border-border-highlight"
            data-testid="personas-create-role"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted">Model</span>
        <ModelAutocomplete
          value={model}
          models={models}
          onChange={setModel}
          onCommit={setModel}
          placeholder="type to search models…"
          testId="personas-create-model"
        />
      </label>
      {!triggerEnabled && (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Pays for tokens</span>
          <select
            value={modelBacking}
            onChange={(e) => setModelBacking(e.target.value as ModelBacking)}
            className="rounded-md border border-edge bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:border-border-highlight"
            data-testid="personas-create-model-backing"
          >
            <option value="openrouter_key">OpenRouter (BYOK)</option>
            <option value="anthropic_key">Anthropic API (BYOK)</option>
            <option value="openai_key">OpenAI API (BYOK)</option>
            <option value="google_key">Google API (BYOK)</option>
            <option value="subscription">
              Subscription (Claude Pro/Max, ChatGPT Plus/Pro)
            </option>
            <option value="pooled">Pooled (Premium)</option>
          </select>
          <p className="text-[0.625rem] text-faint">
            Which key funds this persona's calls. System agents (Vela, Pearl,
            Shore) run on Waytide's pool — never on your keys.
          </p>
        </label>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted">Prompt</span>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder="What does this persona do? What's their voice?"
          className="resize-none rounded-md border border-edge bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:border-border-highlight"
          data-testid="personas-create-prompt"
        />
      </label>
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={triggerEnabled}
          onChange={(e) => setTriggerEnabled(e.target.checked)}
          className="cursor-pointer"
          data-testid="personas-create-trigger-toggle"
        />
        <span className="text-xs text-primary">
          Fires on a watch event (input gate, drift, etc.)
        </span>
      </label>
      {triggerEnabled && (
        <div
          className="flex flex-col gap-3 rounded-md border border-accent/40 bg-accent/5 p-3"
          data-testid="personas-create-trigger-config"
        >
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">
              Trigger template
            </span>
            <select
              value={template}
              onChange={(e) => {
                setTemplate(e.target.value as TriggerTemplateId);
                setTriggerOverridden(false);
              }}
              className="rounded-md border border-edge bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:border-border-highlight"
              data-testid="personas-create-trigger-template"
            >
              {TRIGGER_TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            <p className="text-[0.625rem] text-faint">{tpl.hint}</p>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">Trigger JSON</span>
            <textarea
              value={triggerJson}
              onChange={(e) => {
                setTriggerJson(e.target.value);
                setTriggerOverridden(true);
              }}
              rows={3}
              className="resize-none rounded-md border border-edge bg-surface px-2 py-1.5 font-code text-xs text-primary outline-none focus:border-border-highlight"
              data-testid="personas-create-trigger-json"
            />
          </label>
          <p className="text-[0.625rem] text-faint">
            Token-funding picker is hidden while a trigger is set —
            triggered personas dispatch through the Waytide watcher pool
            for now. Backend split-route follow-up: same model_backing
            picker on triggered personas.
          </p>
        </div>
      )}
      {err && <p className="text-xs text-danger">{err}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="cursor-pointer rounded-md border border-edge px-3 py-1.5 text-xs font-medium text-muted transition-colors hover:text-primary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !displayName.trim() || !model.trim()}
          className="cursor-pointer rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-[var(--wt-selection-text)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="personas-create-submit"
        >
          {busy ? "Creating…" : "Create persona"}
        </button>
      </div>
    </form>
  );
}

function PersonaCard({
  agent,
  currentModel,
  models,
  orgId,
}: {
  agent: AgentCard;
  currentModel: string;
  models: OpenRouterModel[];
  orgId: number | null;
}) {
  const [busy, setBusy] = useState(false);
  const [rerolling, setRerolling] = useState(false);
  const [armedDelete, setArmedDelete] = useState(false);
  const [err, setErr] = useState("");
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (armTimerRef.current) clearTimeout(armTimerRef.current);
    };
  }, []);

  async function onModelChange(next: string) {
    if (next === currentModel) return;
    setBusy(true);
    setErr("");
    try {
      await setAgentModel(
        agent.agent_id,
        next,
        agent.kind === "working"
          ? ((agent.model_backing || "openrouter_key") as ModelBacking)
          : undefined,
      );
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Could not update this agent's model. Retry in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function onBackingChange(next: ModelBacking) {
    if (next === agent.model_backing || !currentModel) return;
    setBusy(true);
    setErr("");
    try {
      await setAgentModel(agent.agent_id, currentModel, next);
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Could not update this agent's model lane. Retry in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function onReroll() {
    if (orgId == null) {
      setErr("Pick an org before regenerating avatars.");
      return;
    }
    setRerolling(true);
    setErr("");
    try {
      await regenerateAgentAvatar(agent.agent_id, orgId);
      triggerBalanceRefresh();
      // Avatar URL lands via SDB subscription; reset busy when the
      // URL changes (or after a short timeout). Keep spinning for a
      // few seconds regardless so the UI reads as "generating…".
      setTimeout(() => setRerolling(false), 15_000);
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Could not kick off avatar generation. Check your OpenRouter key is set in Settings, then retry.",
      );
      setRerolling(false);
    }
  }

  async function onDeleteClick() {
    if (!armedDelete) {
      setArmedDelete(true);
      armTimerRef.current = setTimeout(() => setArmedDelete(false), 3000);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await deleteAgent(agent.agent_id);
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Could not delete this agent. Refresh and try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  // Watch for a new avatar URL arriving — if it does, stop the
  // "generating" spinner early.
  useEffect(() => {
    if (rerolling && agent.avatar_url) {
      // Give it a beat so the image has time to load in the browser.
      setTimeout(() => setRerolling(false), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.avatar_url]);

  const tone = SCORE_TONE[agent.score_state] ?? SCORE_TONE.new;
  const modelMeta = models.find((m) => m.id === currentModel);
  const modelPrice = modelMeta ? formatModelPrice(modelMeta) : null;
  const modelAge = modelMeta ? formatModelAge(modelMeta) : null;

  return (
    <article
      className="relative overflow-hidden rounded-panel border border-edge bg-surface-raised shadow-[var(--wt-shadow)]"
      data-testid={`persona-card-${agent.agent_id}`}
    >
      {/* Accent inner frame — subtle trading-card feel without
       * screaming. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-1 rounded-panel border border-accent/30"
      />

      {/* Portrait zone — solid surface, no gradient */}
      <div className="relative flex h-48 items-center justify-center overflow-hidden bg-surface">
        {agent.avatar_url ? (
          <img
            src={agent.avatar_url}
            alt={`${agent.display_name} portrait`}
            className="h-full w-full object-cover"
            style={{ imageRendering: "pixelated" }}
            data-testid={`persona-card-${agent.agent_id}-avatar`}
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center bg-surface"
            data-testid={`persona-card-${agent.agent_id}-avatar-placeholder`}
          >
            <User size={64} className="text-edge" />
          </div>
        )}
        {/* Re-roll button overlay */}
        <button
          type="button"
          onClick={onReroll}
          disabled={rerolling}
          title="Re-roll avatar"
          aria-label="Re-roll avatar"
          className="absolute left-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-edge bg-surface-raised/80 text-muted backdrop-blur transition-colors hover:border-accent hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          data-testid={`persona-card-${agent.agent_id}-reroll`}
        >
          <RefreshCw size={14} className={rerolling ? "animate-spin" : ""} />
        </button>
        {/* Card number / seat_key in corner */}
        <span className="absolute right-2 top-2 rounded bg-surface-raised/80 px-1.5 py-0.5 font-code text-[0.625rem] text-muted backdrop-blur">
          @{agent.seat_key}
        </span>
      </div>

      {/* Hero text */}
      <div className="px-4 pt-3 pb-1">
        <h3 className="text-base font-semibold text-primary">
          {agent.display_name}
        </h3>
        <p className="mt-0.5 text-xs text-muted">{agent.role_title || "Agent"}</p>
      </div>

      {/* Stat block — more info: model + price + age-of-agent +
       * prompt length + score. Model field is a typeable
       * autocomplete (same pattern as the @ menu). */}
      <div className="flex flex-col gap-2 border-t border-edge px-4 py-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-[0.625rem] font-medium text-muted">Model</span>
          <ModelAutocomplete
            value={currentModel}
            models={models}
            onChange={(next) => void onModelChange(next)}
            onCommit={(next) => void onModelChange(next)}
            disabled={busy}
            testId={`persona-card-${agent.agent_id}-model`}
          />
        </label>

        {agent.kind === "working" ? (
          <label className="flex flex-col gap-0.5">
            <span className="text-[0.625rem] font-medium text-muted">
              Model lane
            </span>
            <select
              value={(agent.model_backing || "openrouter_key") as ModelBacking}
              onChange={(e) =>
                void onBackingChange(e.target.value as ModelBacking)
              }
              disabled={busy || !currentModel}
              className="rounded-md border border-edge bg-surface px-2 py-1.5 text-xs text-primary outline-none focus:border-border-highlight disabled:opacity-50"
              data-testid={`persona-card-${agent.agent_id}-model-backing`}
            >
              {MODEL_BACKING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="rounded bg-surface px-2 py-1 font-code text-[0.625rem] text-muted">
            <span className="mr-1 text-faint">lane:</span>
            {agent.model_backing || "watcher pool"}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-[0.625rem]">
          <StatLine label="Price" value={modelPrice ?? "—"} />
          <StatLine label="Model age" value={modelAge ?? "—"} />
          <StatLine label="Agent age" value={formatAgentAge(agent.created_at)} />
          <StatLine
            label="Prompt"
            value={agent.prompt ? `${agent.prompt.length} chars` : "empty"}
          />
        </div>

        {agent.kind === "watcher" && (
          <WatcherTriggerLine trigger_json={agent.trigger_json} />
        )}

        <ScoreTimeline agentId={agent.agent_id} />

        <div className="flex items-center justify-between pt-1">
          <span
            className={`rounded px-1.5 py-0.5 font-code text-[0.625rem] ${tone}`}
            data-testid={`persona-card-${agent.agent_id}-score`}
          >
            score: {agent.score_state}
          </span>
          <span className="font-code text-[0.625rem] text-faint">
            id #{agent.agent_id}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-edge px-4 py-1.5">
        {agent.kind === "watcher" && (
          <MuteToggle agentId={agent.agent_id} muted={agent.muted} />
        )}
        <button
          onClick={onDeleteClick}
          disabled={busy}
          title={armedDelete ? "Click again to delete" : "Delete agent"}
          aria-label={armedDelete ? "Confirm delete" : "Delete agent"}
          className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            armedDelete
              ? "animate-pulse text-danger"
              : "text-muted hover:text-danger"
          }`}
          data-testid={`persona-card-${agent.agent_id}-delete`}
        >
          {armedDelete ? <AlertTriangle size={14} /> : <Trash2 size={14} />}
        </button>
      </div>
      {err && (
        <p className="px-4 pb-2 text-xs text-danger" data-testid={`persona-card-${agent.agent_id}-error`}>
          {err}
        </p>
      )}
    </article>
  );
}

function ScoreTimeline({ agentId }: { agentId: number }) {
  const [scoreRows] = useTable(tables.score);
  const [rollupRows] = useTable(tables.persona_score_rollup);
  const deltas = useMemo(() => {
    return scoreRows
      .filter(
        (r) =>
          r.subjectKind === "agent" && bigintToNumber(r.subjectId) === agentId,
      )
      .map((r) => ({
        axis: r.axis,
        sub_axis: r.subAxis,
        delta: Number(r.delta),
        confidence: Number(r.confidence),
        at_micros: bigintToNumber(r.atMicros),
      }))
      .sort((a, b) => a.at_micros - b.at_micros);
  }, [scoreRows, agentId]);

  // A15: cross-workspace aggregate via persona_score_rollup.
  const aggregate = useMemo(() => {
    return rollupRows
      .filter((r) => bigintToNumber(r.personaId) === agentId)
      .map((r) => ({
        axis: r.axis,
        sample_size: Number(r.sampleSize),
        cumulative_delta: Number(r.cumulativeDelta),
        confidence: Number(r.confidence),
      }));
  }, [rollupRows, agentId]);

  if (deltas.length === 0 && aggregate.length === 0) {
    return null;
  }

  const byAxis = new Map<string, number>();
  for (const d of deltas) {
    byAxis.set(d.axis, (byAxis.get(d.axis) ?? 0) + d.delta);
  }
  const entries = Array.from(byAxis.entries())
    .map(([axis, total]) => ({ axis, total }))
    .sort((a, b) => a.axis.localeCompare(b.axis));

  return (
    <div
      className="flex flex-wrap items-center gap-1 rounded bg-surface px-2 py-1"
      data-testid={`persona-card-${agentId}-score-timeline`}
    >
      <span className="mr-1 text-[0.625rem] text-faint">axes:</span>
      {entries.map(({ axis, total }) => {
        const tone =
          total > 0.05
            ? "text-success"
            : total < -0.05
              ? "text-danger"
              : "text-muted";
        return (
          <span
            key={axis}
            className={`font-code text-[0.625rem] ${tone}`}
            title={`${axis} · cumulative delta across ${deltas.filter((d) => d.axis === axis).length} scorings`}
          >
            {axis}
            {total > 0 ? "+" : ""}
            {total.toFixed(2)}
          </span>
        );
      })}
      <span className="ml-auto text-[0.625rem] text-faint">
        {deltas.length} scoring{deltas.length === 1 ? "" : "s"}
      </span>
      {aggregate.length > 0 && (
        <div className="mt-1 flex w-full flex-wrap items-center gap-1 border-t border-edge pt-1">
          <span className="mr-1 text-[0.625rem] text-faint">
            cross-workspace:
          </span>
          {aggregate.slice(0, 6).map((a) => {
            const tone =
              a.cumulative_delta > 0.05
                ? "text-success"
                : a.cumulative_delta < -0.05
                  ? "text-danger"
                  : "text-muted";
            return (
              <span
                key={a.axis}
                className={`font-code text-[0.625rem] ${tone}`}
                title={`n=${a.sample_size} · conf=${a.confidence.toFixed(2)}`}
              >
                {a.axis}
                {a.cumulative_delta > 0 ? "+" : ""}
                {a.cumulative_delta.toFixed(2)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WatcherTriggerLine({ trigger_json }: { trigger_json: string }) {
  let summary = "catch-all";
  try {
    const parsed = JSON.parse(trigger_json) as {
      event?: string;
      filter?: Record<string, unknown>;
    };
    const event = parsed.event ?? "working_agent_turn";
    const filter = parsed.filter ?? {};
    const filterKeys = Object.keys(filter);
    if (filterKeys.length === 0) {
      summary = event;
    } else {
      summary = `${event} · ${filterKeys.join(", ")}`;
    }
  } catch {
    summary = "invalid trigger";
  }
  return (
    <div className="rounded bg-surface px-2 py-1 font-code text-[0.625rem] text-muted">
      <span className="mr-1 text-faint">trigger:</span>
      {summary}
    </div>
  );
}

function MuteToggle({ agentId, muted }: { agentId: number; muted: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await setAgentMuted(agentId, !muted);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy}
      title={
        error
          ? error
          : muted
            ? "Unmute this watcher"
            : "Mute this watcher (keeps the card, stops firing)"
      }
      className={`rounded-md border px-2 py-0.5 text-[0.625rem] font-medium transition-colors disabled:opacity-50 ${
        muted
          ? "border-muted text-muted hover:border-accent hover:text-accent"
          : "border-accent text-accent hover:bg-accent/10"
      }`}
      data-testid={`persona-card-${agentId}-mute`}
    >
      {busy ? "…" : muted ? "Muted" : "Armed"}
    </button>
  );
}

function StatLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded bg-surface px-2 py-1">
      <span className="font-medium text-muted">{label}</span>
      <span className="font-code text-[0.6875rem] text-primary">{value}</span>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="rounded-md border border-edge bg-surface-raised px-3 py-3">
      <p className="text-[0.625rem] text-muted">{label}</p>
      <p className="mt-1 text-lg font-semibold text-primary">{value}</p>
      <p className="mt-1 text-[0.625rem] text-faint">{note}</p>
    </div>
  );
}

function formatAgentAge(createdAtMicros: number): string {
  if (!createdAtMicros) return "—";
  const diffMs = Date.now() - createdAtMicros / 1000;
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 1) return "today";
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}
